import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@roger/ui-components';
import { Check } from 'lucide-react';
import type { PatientMatchResult } from '../../hooks/useCreatePatientForm';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { isShellRogerMatch } from './patientMatchUtils';
import {
  MATCH_SHELL_NOT_IMPORTED,
  MATCH_SHELL_SOURCE_LABEL,
} from '../../components/patient/externalEmr/externalEmrPatientCopy';

interface PatientMatchModalProps {
  patientMatchModalOpen: boolean;
  setPatientMatchModalOpen: (open: boolean) => void;
  patientMatches: PatientMatchResult[] | null;
  onSelectPatient: (patient: PatientMatchResult) => void;
  /** When set, preselect the match with this patient_key (e.g. chosen existing patient on intake). */
  preselectedPatientKey?: string | null;
  /** Called when user confirms this is a new patient (not a match). Persist is_new_patient: true. */
  onDontMatch?: () => void | Promise<void>;
  /** Called when user confirms they are matching to an existing patient. Receives the selected match; persist is_new_patient: false and kinnser_matched_patient_key. */
  onMatch?: (patient: PatientMatchResult) => void | Promise<void>;
  /** When false, only "Don't Match" (confirm new patient) is available; Match button and match selection are hidden/disabled. */
  allowSelectExisting?: boolean;
}

export const PatientMatchModal: React.FC<PatientMatchModalProps> = ({
  patientMatchModalOpen,
  setPatientMatchModalOpen,
  patientMatches,
  onSelectPatient,
  preselectedPatientKey,
  onDontMatch: onDontMatch,
  onMatch,
  allowSelectExisting = true,
}) => {
  const [selectedMatch, setSelectedMatch] = useState<PatientMatchResult | null>(
    null
  );

  // When modal opens or matches change: clear selection, or preselect match by patient_key
  useEffect(() => {
    if (patientMatchModalOpen && patientMatches) {
      if (preselectedPatientKey) {
        // A shell is never the existing chart, so it is never preselected.
        const preselected = patientMatches.find(
          m => m.patient_key === preselectedPatientKey && !isShellRogerMatch(m)
        );
        setSelectedMatch(preselected ?? null);
      } else {
        setSelectedMatch(null);
      }
    }
  }, [patientMatchModalOpen, patientMatches, preselectedPatientKey]);

  const handleDontMatch = async () => {
    await onDontMatch?.();
    setPatientMatchModalOpen(false);
  };

  const handleMatch = async () => {
    if (selectedMatch) {
      onSelectPatient(selectedMatch);
      await onMatch?.(selectedMatch);
    }
    setPatientMatchModalOpen(false);
  };

  const isMatchDisabled = selectedMatch == null;

  return (
    <Dialog
      open={patientMatchModalOpen}
      onOpenChange={setPatientMatchModalOpen}
    >
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>Potential Patient Matches Found</DialogTitle>
          <DialogDescription>
            {allowSelectExisting
              ? "Select a patient below to match, or choose Don't Match to continue as a new patient."
              : 'Potential duplicates were found. This patient has been marked as new.'}
          </DialogDescription>
        </DialogHeader>
        <div className='flex-1 overflow-y-auto space-y-3 py-4'>
          {patientMatches?.map((match: PatientMatchResult, index: number) => {
            // A shell has no chart yet. Matching it would attach Roger care to
            // an empty record, so it shows but cannot be picked.
            const isShell = isShellRogerMatch(match);
            const selectable = allowSelectExisting && !isShell;
            const isSelected =
              selectable && selectedMatch != null && selectedMatch === match;
            const MatchWrapper = selectable ? 'button' : 'div';
            return (
              <MatchWrapper
                type={selectable ? 'button' : undefined}
                key={`${match.match_source ?? 'x'}-${match.patient_key}-${index}`}
                onClick={selectable ? () => setSelectedMatch(match) : undefined}
                className={`w-full text-left p-4 border-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <div className='flex items-start gap-3'>
                  {selectable && (
                    <div
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected ? (
                        <Check className='h-3 w-3' strokeWidth={3} />
                      ) : null}
                    </div>
                  )}
                  <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-center gap-2'>
                      <span className='font-medium text-lg'>
                        {match.first_name} {match.middle_initial}{' '}
                        {match.last_name}
                      </span>
                      {isShell ? (
                        <span
                          className='whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'
                          data-testid='match-shell-source'
                        >
                          {MATCH_SHELL_SOURCE_LABEL}
                        </span>
                      ) : match.match_source === 'roger_emr' ? (
                        <span className='rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'>
                          Roger EMR
                        </span>
                      ) : match.match_source === 'kinnser' ? (
                        <span className='rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700'>
                          Kinnser
                        </span>
                      ) : null}
                    </div>
                    <div className='grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600'>
                      <div>
                        <span className='font-medium'>MRN:</span>{' '}
                        {match.mrn || 'N/A'}
                      </div>
                      <div>
                        <span className='font-medium'>DOB:</span>{' '}
                        {formatDateToMMDDYYYY(match.dob) || 'N/A'}
                      </div>
                      <div>
                        <span className='font-medium'>SSN:</span>{' '}
                        {match.ssn ? match.ssn : 'N/A'}
                      </div>
                      <div>
                        <span className='font-medium'>Gender:</span>{' '}
                        {match.gender === '2'
                          ? 'Female'
                          : match.gender === '1'
                            ? 'Male'
                            : 'Unknown'}
                      </div>
                      <div className='col-span-2'>
                        <span className='font-medium'>Address:</span>{' '}
                        {[
                          match.address,
                          match.city,
                          match.state,
                          match.zip_code,
                        ]
                          .filter(Boolean)
                          .join(', ') || 'N/A'}
                      </div>
                    </div>
                    {isShell && (
                      <p
                        className='mt-2 text-sm text-gray-700'
                        data-testid='match-shell-not-imported'
                      >
                        {MATCH_SHELL_NOT_IMPORTED}
                      </p>
                    )}
                  </div>
                </div>
              </MatchWrapper>
            );
          })}
        </div>
        <DialogFooter>
          {allowSelectExisting ? (
            <>
              <Button variant='outline' onClick={handleDontMatch}>
                Don't Match
              </Button>
              <Button onClick={handleMatch} disabled={isMatchDisabled}>
                Match
              </Button>
            </>
          ) : (
            <Button onClick={() => setPatientMatchModalOpen(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
